<script lang="ts">

    import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from "@/components/ui/accordion";
    import {Plus, FileText} from "lucide-svelte";
    import {Button} from "@/components/ui/button";
    import PreviewDocument from "@/components/documents/PreviewDocument.svelte";
    import type Employees from "@/schemas/public/Employees";
    import type Blocks from "@/schemas/public/Blocks";
    import type {Document, Field} from "@/types/types";
    import type {Snippet} from "svelte";
    import * as Card from '@/components/ui/card';
    import { page } from '@inertiajs/svelte';
    import { SvelteSet } from 'svelte/reactivity';

    type Props = {
        children?: Snippet;
        relatedDocuments?: Document[];
        relatedRecordId?: string; // Used to track which record we're viewing
        onOpenCreate?: () => void; // parent can pass in
        blocks?: (Blocks & { fields: Field[] })[];
        id?: string;
    };

    let {children, relatedDocuments = [], relatedRecordId, onOpenCreate, blocks = [], id}: Props = $props();

    // Local additions from flash data (documents added via UI that aren't in props yet)
    let flashAddedDocuments = $state<Document[]>([]);
    let processedFlashIds = new SvelteSet<string>();
    let lastRecordId = $state<string | undefined>(undefined);

    // Reset flash additions when viewing a different record
    $effect(() => {
        if (relatedRecordId !== lastRecordId) {
            flashAddedDocuments = [];
            processedFlashIds.clear();
            lastRecordId = relatedRecordId;
        }
    });

    // Combine prop documents with flash-added documents
    let localDocuments = $derived.by(() => {
        const propDocs = relatedDocuments ?? [];
        const propIds = new Set(propDocs.map(d => d.id));
        // Only include flash-added docs that aren't already in props
        const newFlashDocs = flashAddedDocuments.filter(d => !propIds.has(d.id));
        return [...propDocs, ...newFlashDocs];
    });

    // Handle flash data for document operations
    $effect(() => {
        const flash = $page.props.flash as any;
        if (!flash) return;

        // Handle new document - only if it belongs to this record and hasn't been processed
        if (flash.document && flash.document.related_record_id === relatedRecordId) {
            if (!processedFlashIds.has(flash.document.id)) {
                flashAddedDocuments = [...flashAddedDocuments, flash.document];
                processedFlashIds.add(flash.document.id);
            }
        }
    });

</script>

<Card.Root {id}>
    <Card.Content>
        <Card.Title class="flex items-center justify-between mb-2">
            Related Documents
            <Button
                onclick={onOpenCreate}
                class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent">
                <Plus class="h-4 w-4"/>
                Add Document
            </Button>
        </Card.Title>

        {#if localDocuments && localDocuments.length !== 0}
            {#each localDocuments as document (document.id)}
                <Accordion type="single" collapsible >
                    <AccordionItem value="item-1">
                        <AccordionTrigger class="px-4 my-2" style="background-color: var(--muted);">
                            {document.title} - {document.filename}
                        </AccordionTrigger>
                        <AccordionContent>
                            <PreviewDocument {document} {blocks}/>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            {/each}
        {:else}
            <div class="flex flex-col items-center justify-center py-8 text-center">
                <FileText class="h-12 w-12 text-muted-foreground/50 mb-3" />
                <p class="text-muted-foreground text-sm">No documents attached yet</p>
                <p class="text-muted-foreground text-xs mt-1">Click "Add Document" to upload one</p>
            </div>
        {/if}
    </Card.Content>
</Card.Root>

{@render children?.()}
